import { ZapCompactIcon } from '@vizit/icons';
import { colors, cva, cx } from '@vizit/theme';
import * as React from 'react';
import { Button } from 'react-aria-components';
import { Tooltip, TooltipTrigger } from '../tooltip';

type ImpactLevel = 'very-low' | 'low' | 'medium' | 'high';

interface ImpactBoltProps {
  /**
   * The impact level to display.
   * 'very-low' = 1 bolt, 'low' = 2 bolts, 'medium' = 3 bolts, 'high' = 4 bolts
   */
  level: ImpactLevel;
  /**
   * Whether to hide unfilled bolts, showing only the filled ones.
   * @default false
   */
  hideUnfilled?: boolean;
  /**
   * Whether all bolts render in a disabled/faded color.
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Additional class name for styling
   */
  className?: string;
}

const classes = cva({
  base: 'inline-flex items-center cursor-default',
});

const IMPACT_META: Record<ImpactLevel, { label: string; fillCount: number }> = {
  'very-low': { label: 'Very low impact', fillCount: 1 },
  low: { label: 'Low impact', fillCount: 2 },
  medium: { label: 'Medium impact', fillCount: 3 },
  high: { label: 'High impact', fillCount: 4 },
};

/**
 * A bolt component that displays impact level using zap icons.
 * For `very-low` and `low`, only the filled bolts (1 or 2) are rendered — ghost
 * bolts are suppressed regardless of `hideUnfilled`. For `medium` and `high`,
 * all four positions render (filled or empty), unless `hideUnfilled` is set.
 * Displays a tooltip on hover showing the impact level.
 *
 * @example
 * <ImpactBolt level="medium" />
 */
const ImpactBolt = React.forwardRef<HTMLButtonElement, ImpactBoltProps>((props, forwardedRef) => {
  const { level, hideUnfilled = false, isDisabled = false, className } = props;

  const { label, fillCount } = IMPACT_META[level];
  const showOnlyFilled = hideUnfilled || level === 'very-low' || level === 'low';
  const bolts = showOnlyFilled ? Array.from({ length: fillCount }, (_, i) => i) : [0, 1, 2, 3];
  const disabledColor = colors['gray-utility']['300'];

  return (
    <TooltipTrigger delay={200}>
      <Button ref={forwardedRef} className={cx(classes(), className)} data-testid="impact-bolt">
        {bolts.map((index) => (
          <ZapCompactIcon
            key={index}
            size={14}
            color={
              isDisabled
                ? disabledColor
                : index < fillCount
                  ? colors['gray-utility']['400']
                  : colors['gray-utility']['200']
            }
          />
        ))}
      </Button>
      <Tooltip>{label}</Tooltip>
    </TooltipTrigger>
  );
});

export type { ImpactBoltProps, ImpactLevel };
export { ImpactBolt };
